import ErrorMessage from "@/v2-ui/ErrorMessage"
import { toast } from "@/v2-ui/toast"
import LicenseFeatureGroupAssignmentEditor from "@/v2-backoffice-client/license/featureGroup/LicenseFeatureGroupAssignmentEditor"
import { useAddOnFeatureGroupsSetMutation } from "@/v2-backoffice-client/license/addOns/catalog/license.addOn.featureGroups.set.mutation"
import { evictAddOnCatalogQueries } from "@/v2-backoffice-client/license/license.utils"
import { describeAddOnGoesWith } from "@/v2-backoffice-client/license/addOns/license.addOns.utils"
import type { AddOnCatalogFragment } from "@/v2-backoffice-client/license/addOns/catalog/__types__/license.addOn.catalog.fragment"

export type LicenseAddOnFeatureGroupsTabProps = {
  addOn: AddOnCatalogFragment
}

// The add-on panel's Packages tab: what a customer gets on top of their
// license when they have this add-on. Tick the packages; the selection
// seeds from the catalog row (the board's read carries the membership, so
// opening costs nothing) and saves as one replace-the-set write.
function LicenseAddOnFeatureGroupsTab(
  props: LicenseAddOnFeatureGroupsTabProps,
) {
  const { addOn } = props

  const [setFeatureGroups, { loading, error }] =
    useAddOnFeatureGroupsSetMutation({
      update: evictAddOnCatalogQueries,
      onCompleted() {
        toast({
          variant: "success",
          title: "Packages saved",
          description: `${addOn.Name} was updated.`,
        })
      },
    })

  const persistedIds = addOn.FeatureGroups.map((group) => group.Id)

  const packages = addOn.FeatureGroups

  return (
    <div className="flex h-full min-h-0 flex-col gap-3">
      {/* The one sentence the tab is about, before the pick table: what a
          customer gets from having this add-on. */}
      <p className="text-sm text-muted-foreground">
        A customer with {addOn.Name} gets{" "}
        {packages.length === 0
          ? "nothing yet: tick the packages it should give them."
          : `${packages.length === 1 ? "this package" : `these ${packages.length} packages`} on top of their license: ${packages.map((group) => group.Name).join(", ")}.`}{" "}
        {`${describeAddOnGoesWith(
          addOn.Products.map((product) => product.Name).join(", ") || null,
        )}.`}
      </p>
      <ErrorMessage className="text-center" error={error} />
      <LicenseFeatureGroupAssignmentEditor
        // Keyed on the persisted assignment: when the stored truth changes
        // under the editor (a save landing via evict, another operator's
        // edit), the selection reseeds from it.
        key={persistedIds.join()}
        className="min-h-0 flex-1"
        initialSelectedIds={persistedIds}
        isSaving={loading}
        onSave={(featureGroupIds) => {
          setFeatureGroups({
            variables: { addOnId: addOn.Id, featureGroupIds },
          })
        }}
      />
    </div>
  )
}

export default LicenseAddOnFeatureGroupsTab
